import { Currency } from '@seapoint/server/enums';
import { format } from 'date-fns';
import { Area, AreaChart, CartesianGrid, ReferenceLine, XAxis, YAxis } from 'recharts';
import { ChartConfig, ChartContainer, ChartTooltip } from '../../../../design-system/chart/chart';
import { cn } from '../../../../utils/cn';
import { getAmountWithCurrency } from '../../../../utils/get-amount-with-currency';

export type CurrentCashDataPoint = {
  period: string; // e.g. "19 May", "24 May"
  balance: number; // account balance for the period (in base currency minor units)
  date?: string | Date; // optional ISO date or Date
};

type CurrentCashChartProps = {
  height?: number;
  data: CurrentCashDataPoint[];
  baseCurrency: Currency;
  className?: string;
};

const chartConfig = {
  balance: {
    label: 'Balance',
    color: '#FFE222',
  },
} satisfies ChartConfig;

export const CurrentCashChart = ({ height = 300, data, baseCurrency, className }: CurrentCashChartProps) => {
  const hasNegative = data.some((p) => p.balance < 0);
  const balances = data.map((p) => p.balance);
  const dataMin = balances.length ? Math.min(...balances) : 0;
  const dataMax = balances.length ? Math.max(...balances) : 0;
  let ticks: number[] | undefined;
  if (hasNegative) {
    const count = 5;
    const minVal = Math.min(dataMin, 0);
    const maxVal = Math.max(dataMax, 0);
    const step = (maxVal - minVal) / (count - 1) || 1;
    ticks = Array.from({ length: count }, (_, i) => Math.round(minVal + i * step));
    const zeroIndex = Math.round((0 - minVal) / step);
    if (zeroIndex >= 0 && zeroIndex < count) {
      ticks[zeroIndex] = 0;
    } else if (!ticks.includes(0)) {
      ticks.push(0);
      ticks.sort((a, b) => a - b);
    }
  }
  return (
    <ChartContainer config={chartConfig} className={cn(`h-[${height}px]`, 'w-full', className)}>
      <AreaChart accessibilityLayer data={data} className="p-2">
        <CartesianGrid vertical={false} />
        <XAxis dataKey="period" tickLine={false} axisLine={false} tickMargin={8} interval={0} tick={{ fontSize: 12 }} />
        <YAxis
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tickCount={5}
          domain={hasNegative ? ['dataMin', 'dataMax'] : [0, 'dataMax']}
          ticks={ticks}
          tickFormatter={(value) => {
            const numValue = value as number;
            if (Math.abs(numValue) >= 1000000) {
              return `${baseCurrency === 'GBP' ? '£' : '€'}${(numValue / 1000000).toFixed(1)}M`;
            } else if (Math.abs(numValue) >= 1000) {
              return `${baseCurrency === 'GBP' ? '£' : '€'}${(numValue / 1000).toFixed(0)}K`;
            } else {
              return getAmountWithCurrency({
                amount: numValue,
                currency: baseCurrency,
                isAccountBalance: true,
              });
            }
          }}
        />
        <ChartTooltip
          cursor={false}
          content={({ active, payload, label }) => {
            if (active && payload && payload.length > 0) {
              const dataPoint = payload[0].payload as CurrentCashDataPoint;
              const formattedBalance = getAmountWithCurrency({
                amount: dataPoint.balance,
                currency: baseCurrency,
                isAccountBalance: true,
              });
              const displayLabel = dataPoint.date ? format(new Date(dataPoint.date), 'MMM dd yyyy') : (label as string);

              return (
                <div className="rounded-lg border bg-white p-2 shadow-md">
                  <div className="grid gap-2">
                    <div className="font-medium">{displayLabel}</div>
                    <div className="grid gap-1 text-sm">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-muted-foreground">Account Balance:</span>
                        <span className="font-medium">{formattedBalance}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }
            return null;
          }}
        />
        <defs>
          <linearGradient id="fillCurrentCash" x1="0" y1="0" x2="0" y2="1">
            <stop offset="20%" stopColor={chartConfig.balance.color} stopOpacity={0.8} />
            <stop offset="95%" stopColor="#ffffff" stopOpacity={0.1} />
          </linearGradient>
        </defs>
        <Area
          dataKey="balance"
          type="monotone"
          fill="url(#fillCurrentCash)"
          fillOpacity={0.4}
          stroke={chartConfig.balance.color}
          strokeWidth={2}
        />
        {hasNegative && <ReferenceLine y={0} stroke="#D1D5DB" strokeDasharray="4 4" strokeWidth={1} />}
      </AreaChart>
    </ChartContainer>
  );
};
